import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { useTrackOnce } from "@/hooks/useAnalytics";
import { openCheckout } from "@/components/CheckoutDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  Compass,
  Sparkles,
  ShieldCheck,
  Zap,
  Target,
  Rocket,
  Star,
  Utensils,
  Laptop,
  Wrench,
  HeartPulse,
  Scissors,
  Globe,
  Truck,
  Store,
  ShoppingBag,
  MapPin,
  Coins,
  Briefcase,
  Layers,
  TrendingUp,
  LineChart,
  Lock,
  RefreshCcw,
  BadgeCheck,
  User as UserIcon,
  ChevronDown,
  LayoutDashboard,
  BookOpen,
  Heart,
  LogOut,
} from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Projeto 101 — Descubra o negócio ideal para você" },
      {
        name: "description",
        content:
          "101 negócios reais organizados por investimento, dificuldade, lucro e perfil. Escolha com segurança e comece hoje. Acesso vitalício por R$47.",
      },
      { property: "og:title", content: "Projeto 101 — Descubra o negócio ideal para você" },
      {
        property: "og:description",
        content:
          "A Plataforma Projeto 101 reúne 101 negócios reais organizados por investimento, dificuldade, lucro, operação e perfil empreendedor.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: LandingPage,
});

const CTA_PRIMARY = "QUERO DESCOBRIR MEU NEGÓCIO";
const PRICE = "R$47";

/* ---------------- Nav ---------------- */

function useAuthUser() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(data.session?.user ?? null);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  return { user, ready };
}

function UserMenu({ user }: { user: User }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const firstName =
    (user.user_metadata?.full_name as string | undefined)?.split(" ")[0] ??
    user.email?.split("@")[0] ??
    "Membro";

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest("[data-user-menu]")) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    setOpen(false);
    navigate({ to: "/", replace: true });
  }

  const items = [
    { to: "/membros", label: "Dashboard", icon: LayoutDashboard },
    { to: "/membros/negocios", label: "Biblioteca", icon: BookOpen },
    { to: "/membros/favoritos", label: "Favoritos", icon: Heart },
    { to: "/membros/perfil", label: "Perfil", icon: UserIcon },
  ] as const;

  return (
    <div className="relative" data-user-menu>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 text-sm font-semibold text-[var(--navy)] transition hover:border-[var(--navy)]/25 hover:bg-[var(--gray-soft)]"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--navy)] text-[11px] font-bold uppercase text-white">
          {firstName.charAt(0)}
        </span>
        <span className="hidden max-w-[8rem] truncate capitalize sm:inline">{firstName}</span>
        <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="animate-fade-in absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-black/5 bg-white p-1.5 shadow-[0_20px_40px_-16px_rgba(12,27,51,0.25)]"
        >
          <div className="border-b border-black/5 px-3 py-2 text-xs text-[var(--gray-ink)]">
            {user.email}
          </div>
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--navy)] transition hover:bg-[var(--gray-soft)]"
              role="menuitem"
            >
              <it.icon size={14} className="text-[var(--gray-ink)]" /> {it.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-black/5 px-3 py-2 pt-2.5 text-sm text-[var(--gray-ink)] transition hover:bg-[var(--gray-soft)] hover:text-[var(--navy)]"
            role="menuitem"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

function AccessAction({ user, ready }: { user: User | null; ready: boolean }) {
  if (!ready) {
    return (
      <div className="hidden h-9 w-24 animate-pulse rounded-full bg-[var(--gray-soft)] sm:block" />
    );
  }
  if (user) return <UserMenu user={user} />;
  return (
    <Link
      to="/auth"
      className="group inline-flex items-center gap-1.5 rounded-full border border-[var(--navy)]/15 bg-white/70 px-3 py-2 text-sm font-semibold text-[var(--navy)] backdrop-blur transition hover:border-[var(--navy)]/30 hover:bg-white sm:px-4"
      aria-label="Entrar"
    >
      <UserIcon size={14} />
      <span className="hidden sm:inline">Entrar</span>
    </Link>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { user, ready } = useAuthUser();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-black/5 bg-white/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-5 sm:px-8">
        <a href="#top" className="font-display text-lg font-extrabold tracking-tight text-[var(--navy)]">
          Projeto <span className="text-[var(--green)]">101</span>
        </a>
        <nav className="hidden items-center gap-8 text-sm text-[var(--gray-ink)] md:flex">
          <a href="#plataforma" className="transition hover:text-[var(--navy)]">A Plataforma</a>
          <a href="#categorias" className="transition hover:text-[var(--navy)]">Categorias</a>
          <a href="#como-funciona" className="transition hover:text-[var(--navy)]">Como Funciona</a>
          <a href="#faq" className="transition hover:text-[var(--navy)]">FAQ</a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <AccessAction user={user} ready={ready} />
          <button
            type="button"
            onClick={openCheckout}
            className="group inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(12,27,51,0.5)] transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_12px_28px_-8px_rgba(12,27,51,0.6)]"
          >
            <span className="hidden sm:inline">Acessar por {PRICE}</span>
            <span className="sm:hidden">Acessar</span>
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </header>
  );
}

/* ---------------- Hero ---------------- */

const HERO_BUSINESSES = [
  { name: "Hamburgueria", tag: "Alimentação", investment: "R$ 8k–20k", icon: Utensils },
  { name: "Agência de IA", tag: "Digital", investment: "R$ 0–2k", icon: Sparkles },
  { name: "Barbearia", tag: "Beleza", investment: "R$ 6k–15k", icon: Scissors },
  { name: "Loja Virtual", tag: "E-commerce", investment: "R$ 2k–8k", icon: ShoppingBag },
  { name: "Delivery próprio", tag: "Alimentação", investment: "R$ 3k–10k", icon: Truck },
  { name: "Consultoria", tag: "Serviços", investment: "R$ 0–1k", icon: Briefcase },
];

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-white pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* soft glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 10%, rgba(30,122,92,0.08), transparent 60%), radial-gradient(50% 40% at 90% 20%, rgba(199,154,59,0.09), transparent 60%)",
        }}
      />
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr]">
        <div className="animate-fade-in">
          <span className="inline-flex items-center gap-2 rounded-full border border-black/5 bg-[var(--beige)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--navy)]">
            <Sparkles size={12} className="text-[var(--gold)]" /> Plataforma Projeto 101
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[var(--navy)] sm:text-5xl lg:text-6xl">
            Descubra qual negócio combina com você e comece a construir sua renda.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-[var(--gray-ink)]">
            A Plataforma Projeto 101 reúne 101 negócios reais organizados por investimento, dificuldade,
            lucro, operação e perfil empreendedor — para você escolher com segurança e começar hoje.
          </p>

          <ul className="mt-6 grid max-w-md grid-cols-2 gap-y-2 text-sm text-[var(--navy)]">
            {["101 negócios reais", "Diagnóstico de perfil", "Ferramentas práticas", "Atualizações inclusas"].map(
              (item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--green)]/10 text-[var(--green)]">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ),
            )}
          </ul>

          <div className="mt-8 flex flex-col items-start gap-3">
            <button
              type="button"
              onClick={openCheckout}
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-[var(--green)] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_16px_40px_-12px_rgba(30,122,92,0.55)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_20px_44px_-12px_rgba(30,122,92,0.7)] sm:text-base"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              {CTA_PRIMARY}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[var(--gray-ink)]">
              <span className="inline-flex items-center gap-1"><Check size={12} className="text-[var(--green)]" /> Acesso imediato</span>
              <span className="inline-flex items-center gap-1"><Check size={12} className="text-[var(--green)]" /> Pagamento único</span>
              <span className="inline-flex items-center gap-1"><Check size={12} className="text-[var(--green)]" /> Atualizações inclusas</span>
            </div>
            <p className="mt-1 text-sm text-[var(--gray-ink)]">
              Já possui acesso?{" "}
              <Link
                to="/auth"
                className="font-semibold text-[var(--navy)] underline decoration-[var(--green)]/40 decoration-2 underline-offset-4 transition hover:text-[var(--green)] hover:decoration-[var(--green)]"
              >
                Entrar
              </Link>
            </p>
          </div>
        </div>

        <HeroMockup />
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative mx-auto w-full max-w-[520px]">
      {/* floating accents */}
      <div aria-hidden className="absolute -left-6 -top-6 h-32 w-32 rounded-full bg-[var(--gold)]/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-8 -right-6 h-40 w-40 rounded-full bg-[var(--green)]/20 blur-3xl" />

      <div className="relative rounded-[28px] border border-black/5 bg-white/80 p-4 shadow-[0_40px_80px_-30px_rgba(12,27,51,0.35)] backdrop-blur-xl">
        {/* window bar */}
        <div className="flex items-center gap-1.5 px-2 py-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 flex-1 truncate rounded-md bg-[var(--gray-soft)] px-2 py-1 text-[10px] text-[var(--gray-ink)]">
            plataforma.projeto101.com.br/negocios
          </div>
        </div>

        {/* filters row */}
        <div className="mt-3 flex flex-wrap gap-1.5 px-1">
          {["Todos", "Baixo investimento", "Digital", "Local"].map((t, i) => (
            <span
              key={t}
              className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${
                i === 0
                  ? "bg-[var(--navy)] text-white"
                  : "border border-black/5 bg-white text-[var(--gray-ink)]"
              }`}
            >
              {t}
            </span>
          ))}
        </div>

        {/* card grid */}
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          {HERO_BUSINESSES.map(({ name, tag, investment, icon: Icon }, i) => (
            <div
              key={name}
              className="group rounded-2xl border border-black/5 bg-white p-3 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--beige)] text-[var(--navy)]">
                  <Icon size={15} />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--green)]">
                  {tag}
                </span>
              </div>
              <p className="mt-2 font-display text-sm font-extrabold text-[var(--navy)]">{name}</p>
              <p className="mt-0.5 text-[10px] text-[var(--gray-ink)]">Investimento {investment}</p>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((n) => (
                    <span
                      key={n}
                      className={`h-1 w-4 rounded-full ${
                        n <= 3 ? "bg-[var(--green)]" : "bg-[var(--gray-soft)]"
                      }`}
                    />
                  ))}
                </div>
                <ArrowRight size={12} className="text-[var(--gray-ink)] transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          ))}
        </div>

        {/* floating badge */}
        <div className="absolute -right-3 -top-3 hidden rounded-2xl border border-black/5 bg-white px-3 py-2 shadow-lg sm:block">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--gold)]/15 text-[var(--gold)]">
              <BadgeCheck size={16} />
            </span>
            <div className="text-[10px] leading-tight">
              <p className="font-semibold text-[var(--navy)]">Perfil validado</p>
              <p className="text-[var(--gray-ink)]">Consultoria + IA</p>
            </div>
          </div>
        </div>

        <div className="absolute -bottom-4 -left-4 hidden rounded-2xl border border-black/5 bg-white px-3 py-2 shadow-lg sm:block">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--green)]/10 text-[var(--green)]">
              <TrendingUp size={16} />
            </span>
            <div className="text-[10px] leading-tight">
              <p className="font-semibold text-[var(--navy)]">Meta R$ 10k</p>
              <p className="text-[var(--gray-ink)]">Simulação pronta</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Social Proof ---------------- */

function SocialProof() {
  const items = [
    { top: "★★★★★", bottom: "4.9/5 de avaliação" },
    { top: "+3.000", bottom: "empreendedores" },
    { top: "101", bottom: "negócios disponíveis" },
    { top: "∞", bottom: "atualizações constantes" },
  ];
  return (
    <section className="border-y border-black/5 bg-white py-8">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-6 px-5 sm:px-8 md:grid-cols-4">
        {items.map((it) => (
          <div key={it.bottom} className="text-center">
            <p className="font-display text-xl font-extrabold text-[var(--navy)]">{it.top}</p>
            <p className="mt-0.5 text-xs uppercase tracking-wider text-[var(--gray-ink)]">{it.bottom}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Big numbers ---------------- */

function BigNumbers() {
  const stats = [
    { n: "101", l: "Negócios reais" },
    { n: "35+", l: "Categorias" },
    { n: "500+", l: "Ferramentas" },
    { n: "∞", l: "Atualizações" },
    { n: "1", l: "Diagnóstico personalizado" },
  ];
  return (
    <section id="plataforma" className="bg-[var(--navy)] py-20 text-white sm:py-28">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-label text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">A plataforma em números</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
            Uma biblioteca curada. Um método claro. Zero achismo.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-5">
          {stats.map((s) => (
            <div key={s.l} className="border-l border-white/10 pl-4">
              <p className="font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl">
                {s.n}
              </p>
              <p className="mt-2 text-xs uppercase tracking-wider text-white/60">{s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- How it works ---------------- */

function HowItWorks() {
  const steps = [
    { n: "01", t: "Descubra seu perfil", d: "Faça o diagnóstico e entenda qual tipo de negócio se encaixa em você.", icon: Compass },
    { n: "02", t: "Explore os negócios", d: "Navegue por 101 fichas reais, filtradas por investimento e dificuldade.", icon: Layers },
    { n: "03", t: "Escolha o mais adequado", d: "Compare, favorite e valide com nossas ferramentas de simulação.", icon: Target },
    { n: "04", t: "Comece hoje", d: "Siga o passo a passo e conquiste seus primeiros clientes em dias.", icon: Rocket },
  ];
  return (
    <section id="como-funciona" className="bg-white py-24 sm:py-32">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-label text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">Como funciona</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight text-[var(--navy)] sm:text-4xl">
            Do "não sei por onde começar" ao primeiro cliente em 4 passos.
          </h2>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="group relative rounded-2xl border border-black/5 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(12,27,51,0.25)]"
            >
              <div className="flex items-center justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--beige)] text-[var(--navy)] transition-colors group-hover:bg-[var(--green)] group-hover:text-white">
                  <s.icon size={20} />
                </span>
                <span className="font-display text-3xl font-extrabold text-[var(--gray-soft)] group-hover:text-[var(--gold)]">
                  {s.n}
                </span>
              </div>
              <h3 className="mt-5 font-display text-lg font-extrabold text-[var(--navy)]">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[var(--gray-ink)]">{s.d}</p>
              {i < steps.length - 1 && (
                <ArrowRight
                  size={16}
                  className="absolute -right-3 top-1/2 hidden -translate-y-1/2 text-[var(--gray-soft)] lg:block"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Categories ---------------- */

function Categories() {
  const cats = [
    { n: "Alimentação", icon: Utensils, c: "12 negócios" },
    { n: "Tecnologia", icon: Laptop, c: "10 negócios" },
    { n: "Serviços", icon: Wrench, c: "14 negócios" },
    { n: "Saúde", icon: HeartPulse, c: "6 negócios" },
    { n: "Estética", icon: Scissors, c: "8 negócios" },
    { n: "Mercado Digital", icon: Globe, c: "11 negócios" },
    { n: "Delivery", icon: Truck, c: "7 negócios" },
    { n: "E-commerce", icon: ShoppingBag, c: "9 negócios" },
    { n: "Negócios locais", icon: MapPin, c: "10 negócios" },
    { n: "Renda extra", icon: Coins, c: "8 negócios" },
    { n: "Consultoria", icon: Briefcase, c: "6 negócios" },
    { n: "Marketplace", icon: Store, c: "5 negócios" },
  ];
  return (
    <section id="categorias" className="bg-[var(--beige)]/40 py-24 sm:py-32">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-label text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">Categorias</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight text-[var(--navy)] sm:text-4xl">
            Um universo de possibilidades — organizado para você não se perder.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {cats.map((c) => (
            <div
              key={c.n}
              className="group cursor-pointer rounded-2xl border border-black/5 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--green)]/30 hover:shadow-[0_16px_36px_-20px_rgba(12,27,51,0.25)]"
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--gray-soft)] text-[var(--navy)] transition-colors group-hover:bg-[var(--navy)] group-hover:text-white">
                <c.icon size={18} />
              </span>
              <p className="mt-4 font-display text-base font-extrabold text-[var(--navy)]">{c.n}</p>
              <p className="mt-0.5 text-xs text-[var(--gray-ink)]">{c.c}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Differentials ---------------- */

function Differentials() {
  const items = [
    { i: ShieldCheck, t: "Sem promessas irreais", d: "Nada de 'fique rico em 30 dias'. Só dados e caminhos reais." },
    { i: Layers, t: "Organizado por investimento", d: "Filtre por quanto você pode investir e comece pelo possível." },
    { i: BadgeCheck, t: "Negócios validados", d: "Cada ficha passou por curadoria editorial e revisão de mercado." },
    { i: RefreshCcw, t: "Atualizações constantes", d: "A biblioteca evolui com o mercado. Você acompanha para sempre." },
    { i: Compass, t: "Diagnóstico inteligente", d: "Descubra em minutos o negócio que combina com o seu perfil." },
    { i: Zap, t: "Ferramentas práticas", d: "Calculadora 10K, plano de 30 dias, checklists e simuladores." },
    { i: LineChart, t: "Sem enrolação", d: "Fichas diretas, com passo a passo real e sem rodeios." },
    { i: Lock, t: "Acesso vitalício", d: "Pagamento único. Sem mensalidade. Sem letras miúdas." },
  ];
  return (
    <section className="bg-white py-24 sm:py-32">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-label text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">Por que Projeto 101</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight text-[var(--navy)] sm:text-4xl">
            O que torna a plataforma diferente de tudo que você já viu.
          </h2>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ i: Icon, t, d }) => (
            <div
              key={t}
              className="rounded-2xl border border-black/5 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--green)]/20 hover:shadow-[0_16px_36px_-20px_rgba(12,27,51,0.2)]"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--green)]/10 text-[var(--green)]">
                <Icon size={20} />
              </span>
              <h3 className="mt-5 font-display text-base font-extrabold text-[var(--navy)]">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[var(--gray-ink)]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing / Offer ---------------- */

function Offer() {
  return (
    <section className="relative overflow-hidden bg-[var(--navy)] py-24 text-white sm:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60% 50% at 20% 30%, rgba(30,122,92,0.35), transparent 60%), radial-gradient(50% 50% at 80% 70%, rgba(199,154,59,0.25), transparent 60%)",
        }}
      />
      <div className="relative mx-auto w-full max-w-3xl px-5 text-center sm:px-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 backdrop-blur">
          <Sparkles size={12} className="text-[var(--gold)]" /> Acesso vitalício
        </span>
        <h2 className="mt-6 font-display text-4xl font-extrabold leading-tight sm:text-5xl">
          Pague uma vez. Use para sempre.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-white/70">
          Um investimento único para ter em mãos a plataforma completa, o diagnóstico, as ferramentas e todas
          as atualizações futuras.
        </p>

        <div className="mx-auto mt-10 max-w-md rounded-3xl border border-white/10 bg-white/5 p-8 text-left backdrop-blur-xl">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-5xl font-extrabold tracking-tight text-white">R$47</span>
            <span className="text-sm text-white/60">à vista · ou 12x</span>
          </div>
          <p className="mt-1 text-xs uppercase tracking-wider text-[var(--gold)]">Pagamento único · acesso imediato</p>

          <ul className="mt-6 space-y-3 text-sm">
            {[
              "101 fichas de negócios reais",
              "Diagnóstico de perfil empreendedor",
              "Calculadora 10K e simuladores",
              "Plano de 30 dias e checklists",
              "Atualizações vitalícias inclusas",
              "Garantia incondicional de 7 dias",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[var(--green)] text-white">
                  <Check size={10} strokeWidth={3} />
                </span>
                <span className="text-white/90">{f}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={openCheckout}
            className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--green)] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_16px_40px_-12px_rgba(30,122,92,0.7)] transition-all duration-300 hover:scale-[1.02]"
          >
            LIBERAR MEU ACESSO
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <p className="mt-3 text-center text-[11px] text-white/50">
            Pagamento 100% seguro · PIX ou cartão em até 12x
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

function FAQ() {
  const items = [
    ["O que exatamente é a Plataforma 101?", "É uma plataforma online com 101 negócios reais catalogados, um diagnóstico de perfil empreendedor e ferramentas práticas para você escolher e começar o seu negócio com segurança."],
    ["Preciso ter experiência para usar?", "Não. A plataforma foi desenhada para quem está começando do zero e também para quem já empreende e quer explorar novos caminhos."],
    ["Como recebo o acesso?", "Imediatamente após a confirmação do pagamento você recebe um e-mail com o login e a senha da sua conta na plataforma."],
    ["É pagamento único ou mensalidade?", "Pagamento único de R$47. Sem mensalidade, sem renovação, com atualizações inclusas para sempre."],
    ["Posso parcelar?", "Sim. Você pode pagar via PIX ou cartão em até 12x."],
    ["Vocês ensinam algum negócio específico?", "Sim. Cada uma das 101 fichas apresenta passo a passo, investimento, público, ferramentas, canais de aquisição e primeiros passos."],
    ["Serve para quem mora em cidade pequena?", "Sim. Vários negócios da biblioteca funcionam melhor em cidades menores — de serviços locais a delivery."],
    ["Preciso investir muito para começar?", "Não. Há negócios com investimento inicial próximo de zero e outros que exigem capital. Você filtra pelo que faz sentido para o seu momento."],
    ["Recebo atualizações futuras?", "Sim. Todas as novas fichas e ferramentas que forem adicionadas ficam disponíveis na sua conta sem custo adicional."],
    ["Funciona no celular?", "Sim. A plataforma é totalmente responsiva e foi otimizada para uso no celular."],
    ["Vocês oferecem garantia?", "Sim. Você tem 7 dias de garantia incondicional. Se não gostar, devolvemos 100% do valor sem perguntas."],
    ["Como falo com o suporte?", "Pelo e-mail suporte@101negocios.com.br, respondido em dias úteis."],
  ] as const;

  return (
    <section id="faq" className="bg-white py-24 sm:py-32">
      <div className="mx-auto w-full max-w-3xl px-5 sm:px-8">
        <div className="text-center">
          <span className="font-label text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">Perguntas frequentes</span>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight text-[var(--navy)] sm:text-4xl">
            Tudo o que você precisa saber antes de começar.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 w-full">
          {items.map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`} className="border-b border-black/5">
              <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-[var(--navy)] hover:no-underline">
                {q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-[var(--gray-ink)]">
                {a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */

function FinalCTA() {
  return (
    <section className="bg-[var(--beige)] py-20 sm:py-28">
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-6 px-5 text-center sm:px-8">
        <Star size={22} className="text-[var(--gold)]" />
        <h2 className="font-display text-3xl font-extrabold leading-tight text-[var(--navy)] sm:text-4xl">
          O próximo negócio da sua vida pode estar a um clique.
        </h2>
        <p className="max-w-xl text-[var(--gray-ink)]">
          Junte-se a milhares de empreendedores que já encontraram sua próxima oportunidade dentro da Plataforma 101.
        </p>
        <button
          type="button"
          onClick={openCheckout}
          className="group mt-2 inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_16px_40px_-12px_rgba(12,27,51,0.6)] transition-all duration-300 hover:scale-[1.02]"
        >
          {CTA_PRIMARY}
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </button>
        <p className="text-xs text-[var(--gray-ink)]">Acesso imediato · Pagamento único de {PRICE} · 7 dias de garantia</p>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */

function Footer() {
  return (
    <footer className="border-t border-black/5 bg-white py-12">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-start justify-between gap-6 px-5 sm:px-8 md:flex-row md:items-center">
        <div>
          <p className="font-display text-lg font-extrabold text-[var(--navy)]">
            Projeto <span className="text-[var(--green)]">101</span>
          </p>
          <p className="mt-1 text-xs text-[var(--gray-ink)]">
            © {new Date().getFullYear()} Projeto 101. Todos os direitos reservados.
          </p>
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[var(--gray-ink)]">
          <a href="/termos" className="hover:text-[var(--navy)]">Termos de uso</a>
          <a href="/privacidade" className="hover:text-[var(--navy)]">Privacidade</a>
          <a href="/reembolso" className="hover:text-[var(--navy)]">Reembolso</a>
          <a href="/aviso-legal" className="hover:text-[var(--navy)]">Aviso legal</a>
          <a href="mailto:suporte@101negocios.com.br" className="hover:text-[var(--navy)]">Suporte</a>
        </nav>
      </div>
    </footer>
  );
}

/* ---------------- Page ---------------- */

function LandingPage() {
  useTrackOnce("landing_view");

  return (
    <main className="min-h-screen bg-white font-body text-[var(--navy)]">
      <Nav />
      <Hero />
      <SocialProof />
      <BigNumbers />
      <HowItWorks />
      <Categories />
      <Differentials />
      <Offer />
      <FAQ />
      <FinalCTA />
      <Footer />
    </main>
  );
}
